import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { CopcPointCloud } from '../../../../../../viewer/geodata/point-clouds/copc-point-cloud/copc-point-cloud';
import { LabelledContainer } from '../../../../labelled-container/labelled-container';
import { Slider } from '../../../../slider/slider';
import { Switch } from '../../../../switch/switch';

/** Top-down needs the same level across the view, so it gets twice the perspective budget. */
const ORTHOGRAPHIC_BUDGET_FACTOR = 2;

const formatMillions = (points: number) => (points / 1e6).toFixed(1);

/**
 * How much of a COPC point cloud loads and how its points are sized, for organization admins to
 * tune while COPC is evaluated. The settings hold for every COPC point cloud in the session.
 */
const CopcSettings = () => {
  const { t } = useTranslation();
  const { settings } = CopcPointCloud;
  const [pointBudget, setPointBudget] = React.useState(settings.pointBudget.perspective);
  const [maxSpacingPixels, setMaxSpacingPixels] = React.useState(settings.maxSpacingPixels);
  const [adaptivePointSize, setAdaptivePointSize] = React.useState(settings.adaptivePointSize);
  const [stats, setStats] = React.useState(CopcPointCloud.current?.stats);

  useEffect(() => {
    const interval = setInterval(() => setStats(CopcPointCloud.current?.stats), 1000);
    return () => clearInterval(interval);
  }, []);

  const onPointBudget = (millions: number) => {
    const points = Math.round(millions * 1e6);
    CopcPointCloud.settings.pointBudget = {
      perspective: points,
      orthographic: points * ORTHOGRAPHIC_BUDGET_FACTOR,
    };
    setPointBudget(points);
  };

  const onMaxSpacingPixels = (pixels: number) => {
    CopcPointCloud.settings.maxSpacingPixels = pixels;
    setMaxSpacingPixels(pixels);
  };

  const onAdaptivePointSize = (e: React.MouseEvent<HTMLInputElement>) => {
    const enabled = (e.target as HTMLInputElement).checked;
    CopcPointCloud.settings.adaptivePointSize = enabled;
    setAdaptivePointSize(enabled);
  };

  return (
    <>
      <LabelledContainer
        noWrap={true}
        text={t('pointcloud.settings.copc.title', { ns: 'skyviewToolbar' })}
      >
        <span>
          {stats &&
            t('pointcloud.settings.copc.stats', {
              ns: 'skyviewToolbar',
              drawn: formatMillions(stats.drawnPoints),
              nodes: stats.drawnNodes,
              downloaded: stats.downloadedMb.toFixed(1),
            })}
        </span>
      </LabelledContainer>
      <LabelledContainer
        noWrap={true}
        text={t('pointcloud.settings.copc.pointBudget.title', {
          ns: 'skyviewToolbar',
          value: formatMillions(pointBudget),
        })}
      >
        <Slider max={5} min={0.1} step={0.1} value={pointBudget / 1e6} onChange={onPointBudget} />
      </LabelledContainer>
      <LabelledContainer
        noWrap={true}
        text={t('pointcloud.settings.copc.maxSpacing.title', {
          ns: 'skyviewToolbar',
          value: maxSpacingPixels.toFixed(1),
        })}
      >
        <Slider
          max={10}
          min={1}
          step={0.5}
          value={maxSpacingPixels}
          onChange={onMaxSpacingPixels}
        />
      </LabelledContainer>
      <LabelledContainer
        noWrap={true}
        text={t('pointcloud.settings.copc.adaptivePointSize.title', { ns: 'skyviewToolbar' })}
      >
        <Switch checked={adaptivePointSize} onChange={onAdaptivePointSize} />
      </LabelledContainer>
    </>
  );
};

export { CopcSettings };
